import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { usePros, useServices, type Professional, type Service } from '../lib/data.ts';
import { addDays, durLabel, first, initials, moneyShort, nowLocal, tone } from '../lib/format.ts';
import { CfgChips, CfgNum, CfgText, CfgToggle, Saved } from '../ui/cfg.tsx';
import { BackHeader } from '../ui/Header.tsx';
import { Icon } from '../ui/Icon.tsx';
import { AGENDA_COLORS } from '../lib/agenda.ts';

const COLOR_NAMES: Record<string, string> = { '#D97980': 'Rosé', '#8C7A70': 'Bege', '#6E8F86': 'Sálvia', '#B08A5A': 'Caramelo', '#7C6A9A': 'Lavanda', '#5F7E9B': 'Azul' };
import { Sheet } from '../ui/Sheet.tsx';

// ---------- serviços ----------
export function Servicos() {
  const nav = useNav();
  const list = useServices().data ?? [];
  const cats = [...new Set(list.map((s) => s.category || 'Outros'))];
  return (
    <div className="view-enter cp2"><BackHeader title="Serviços" label="Configurações" />
      <div className="cp2-body">
        <p className="ac-intro">Toque para mudar preço, duração, limpeza e retorno.</p>
        {cats.map((c) => (
          <div key={c}>
            <p className="gl-title">{c}</p>
            <div className="gl">
              {list.filter((s) => (s.category || 'Outros') === c).map((x) => (
                <button key={x.id} className="gl-row gl-btn pm-maq sv-row" onClick={() => nav.go('servico', { id: x.id })}>
                  <b>{x.name}<small>{durLabel(x.durationMin)}{x.cleanupMin ? ` + ${x.cleanupMin} min de limpeza` : ''}{x.returnDays ? ` · retorno em ${x.returnDays} dias` : ''}{x.bookableOnline ? '' : ' · fora do link'}{x.active ? '' : ' · desativado'}</small></b>
                  <em className="sv-price">{x.priceFrom ? 'a partir de ' : ''}{moneyShort(x.priceCents)}</em><Icon name="chevR" />
                </button>
              ))}
            </div>
          </div>
        ))}
        <div className="gl" style={{ marginTop: 12 }}><button className="gl-row gl-btn ac-invite" onClick={() => nav.open({ kind: 'modelos' })}><b>+ Adicionar serviço</b></button></div>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

const svcBody = (x: Service) => ({ name: x.name, category: x.category, description: x.description, durationMin: x.durationMin, cleanupMin: x.cleanupMin, priceCents: x.priceCents, priceFrom: x.priceFrom, returnDays: x.returnDays, bookableOnline: x.bookableOnline, active: x.active, professionalIds: x.professionalIds, version: x.version });

export function Servico() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const list = useServices().data ?? [];
  const pros = (usePros().data ?? []).filter((p) => p.active);
  const x = list.find((s) => s.id === nav.params.id);
  const [savedAt, setSavedAt] = useState(0);
  if (!x) return <div className="view-enter cp2"><BackHeader title="Serviço" label="Serviços" /></div>;
  const save = async (patch: Partial<Service>) => {
    try { await api.put(`/services/${x.id}`, { ...svcBody(x), ...patch }); await qc.invalidateQueries({ queryKey: ['services'] }); setSavedAt(Date.now()); }
    catch (e) { toast(msg(e), 'warn'); await qc.invalidateQueries({ queryKey: ['services'] }); }
  };
  const who = x.professionalIds.length ? x.professionalIds : pros.map((p) => p.id);
  return (
    <div className="view-enter cp2"><BackHeader title={x.name} label="Serviços" />
      <div className="cp2-body">
        <p className="gl-title">Serviço<Saved at={savedAt} /></p>
        <div className="gl">
          <CfgText label="Nome" value={x.name} onChange={(v) => v && save({ name: v })} />
          <CfgText label="Categoria" value={x.category} onChange={(v) => save({ category: v })} />
          <CfgNum label="Preço" kind="money" value={x.priceCents} lim={[0, 200_000, 500]} onChange={(v) => save({ priceCents: v })} />
          <CfgToggle label='Preço "a partir de"' value={x.priceFrom} onChange={(v) => save({ priceFrom: v })} sub="quando varia com o tamanho ou a arte" />
          <CfgNum label="Duração" unit="min" value={x.durationMin} lim={[10, 300, 15]} onChange={(v) => save({ durationMin: v })} />
          <CfgNum label="Limpeza depois" unit="min" value={x.cleanupMin} lim={[0, 60, 5]} onChange={(v) => save({ cleanupMin: v })} hint="esterilizar e arrumar a mesa" />
        </div>
        <p className="gl-title">Retorno</p>
        <div className="gl"><CfgNum label="Retorno sugerido" unit="dias" value={x.returnDays} lim={[0, 120, 1]} onChange={(v) => save({ returnDays: v })} hint={x.returnDays ? 'a cliente entra em "manutenção vencendo"' : '0 = sem lembrete'} /></div>
        <p className="gl-title">Agendamento</p>
        <div className="gl">
          <CfgToggle label="Aparece no link de agendamento" value={x.bookableOnline} onChange={(v) => save({ bookableOnline: v })} />
          <div className="gl-block"><span>Quem faz</span><div className="chipset soft">
            {pros.map((p) => {
              const on = who.includes(p.id);
              return <button key={p.id} className={on ? 'on' : ''} aria-pressed={on} onClick={() => {
                const next = on ? who.filter((i) => i !== p.id) : [...who, p.id];
                if (!next.length) return toast('Pelo menos uma pessoa precisa fazer o serviço.', 'warn');
                save({ professionalIds: next.length === pros.length ? [] : next });
              }}>{first(p.name)}</button>;
            })}
          </div></div>
        </div>
        <button className="link-back pm-del" onClick={async () => { await save({ active: !x.active }); }}>{x.active ? 'Desativar serviço (o histórico continua)' : 'Reativar serviço'}</button>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

const MODELOS = [
  { name: 'Esmaltação em gel', category: 'Mão', durationMin: 60, priceCents: 8000, cleanupMin: 10, returnDays: 21 },
  { name: 'Alongamento em gel', category: 'Alongamento', durationMin: 150, priceCents: 18_000, priceFrom: true, cleanupMin: 15, returnDays: 21 },
  { name: 'Alongamento em fibra', category: 'Alongamento', durationMin: 180, priceCents: 20_000, priceFrom: true, cleanupMin: 15, returnDays: 21 },
  { name: 'Manutenção do alongamento', category: 'Alongamento', durationMin: 90, priceCents: 12_000, cleanupMin: 10, returnDays: 21 },
  { name: 'Banho de gel', category: 'Mão', durationMin: 75, priceCents: 9000, cleanupMin: 10, returnDays: 21 },
  { name: 'Blindagem', category: 'Mão', durationMin: 60, priceCents: 7000, cleanupMin: 10, returnDays: 21 },
  { name: 'Remoção de gel', category: 'Mão', durationMin: 30, priceCents: 3000, cleanupMin: 5, returnDays: 0 },
  { name: 'Nail art (por unha)', category: 'Arte', durationMin: 10, priceCents: 500, priceFrom: true, cleanupMin: 0, returnDays: 0 },
];

export function ModelosSheet() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const have = new Set((useServices().data ?? []).map((s) => s.name.toLowerCase()));
  const add = async (m: Partial<Service> & { name: string }) => {
    try {
      let name = m.name, n = 2;
      while (have.has(name.toLowerCase())) name = `${m.name} ${n++}`;
      const s = await api.post<Service>('/services', { category: '', durationMin: 60, priceCents: 5000, ...m, name });
      await qc.invalidateQueries({ queryKey: ['services'] });
      nav.go('servico', { id: s.id }); toast(`${name} adicionado. Ajuste o que precisar.`);
    } catch (e) { toast(msg(e), 'warn'); }
  };
  return (
    <Sheet title="Adicionar serviço" actions={<button className="link-back" onClick={nav.close}>Fechar</button>}>
      <p className="m-hint an-intro">Modelos comuns em nail design, já com duração, limpeza e retorno. Depois de adicionar, ajuste o preço.</p>
      <div className="gl">
        {MODELOS.map((m) => {
          const tem = have.has(m.name.toLowerCase());
          return <button key={m.name} className="gl-row gl-btn pm-maq" disabled={tem} onClick={() => add(m)}><b>{m.name}<small>{durLabel(m.durationMin)} · {m.priceFrom ? 'a partir de ' : ''}{moneyShort(m.priceCents)}{m.returnDays ? ` · retorno em ${m.returnDays} dias` : ''}</small></b>{tem ? <em className="mais-ver">Já tem</em> : <Icon name="plus" />}</button>;
        })}
        <button className="gl-row gl-btn ac-invite" onClick={() => add({ name: 'Serviço novo', category: '', durationMin: 60, priceCents: 5000 })}><b>+ Serviço em branco</b></button>
      </div>
    </Sheet>
  );
}

// ---------- parceiras ----------
export function Equipe() {
  const nav = useNav();
  const pros = usePros().data ?? [];
  return (
    <div className="view-enter cp2"><BackHeader title="Parceiras" label="Configurações" />
      <div className="cp2-body">
        <p className="ac-intro">Cada parceira tem a própria cota (Lei do Salão-Parceiro). O salão recebe tudo, retém a parte dele e repassa a cota.</p>
        <div className="gl">
          {pros.map((p) => (
            <button key={p.id} className="gl-row gl-btn ac-member" onClick={() => nav.go('parceria', { id: p.id })}>
              <span className={`av t-${tone(p.id)}`}>{initials(p.name)}</span>
              <b>{p.name}<small>{p.isOwner ? 'dona' : cotaLabel(p)}{p.active ? '' : ' · desativada'}</small></b><Icon name="chevR" />
            </button>
          ))}
          <button className="gl-row gl-btn ac-invite" onClick={() => nav.go('acessos')}><b>+ Convidar parceira (Perfis de acesso)</b><Icon name="chevR" /></button>
        </div>
        <button className="link-back" onClick={() => nav.go('repasse')}>Ver saldos e repasses</button>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}
const cotaLabel = (p: Professional) => (!p.cota ? '' : p.cota.kind === 'fixed' ? `cota fixa de ${p.cota.bps / 100}%` : `cota escalonada · ${p.cota.tiers.length} faixas`);

interface FullPro extends Professional { productBps: number; payoutPeriod: string; document: string; pixKey: string; bonus: { enabled: boolean; goalCents: number; kind: 'value' | 'pct'; valueCents: number; bps: number }; version: number }

export function Parceria() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['professionals'], queryFn: () => api.get<FullPro[]>('/professionals') });
  const p = q.data?.find((x) => x.id === nav.params.id);
  const [savedAt, setSavedAt] = useState(0);
  if (!p || !p.cota) return <div className="view-enter cp2"><BackHeader title="Parceria" label="Parceiras" /></div>;
  const nm = first(p.name);
  const save = async (patch: Partial<FullPro>) => {
    try {
      await api.put(`/professionals/${p.id}`, { name: p.name, role: p.role, color: p.color, cota: p.cota, bonus: p.bonus, productBps: p.productBps, payoutPeriod: p.payoutPeriod, workHours: p.workHours, document: p.document, pixKey: p.pixKey, active: p.active, version: p.version, ...patch });
      await qc.invalidateQueries({ queryKey: ['professionals'] }); setSavedAt(Date.now());
    } catch (e) { toast(msg(e), 'warn'); await qc.invalidateQueries({ queryKey: ['professionals'] }); }
  };
  const c = p.cota;
  const tiers = c.kind === 'tiered' ? c.tiers : [{ upToCents: 800_000, bps: c.bps }, { upToCents: null, bps: Math.min(9500, c.bps + 500) }];
  const setTiers = (t: typeof tiers) => save({ cota: { kind: 'tiered', tiers: t } });
  const b = p.bonus ?? { enabled: false, goalCents: 1_000_000, kind: 'value', valueCents: 20_000, bps: 500 };
  return (
    <div className="view-enter cp2"><BackHeader title={`Parceria · ${p.name}`} label="Parceiras" />
      <div className="cp2-body">
        <p className="ac-intro">{p.isOwner ? 'A dona também atende. A cota dela pode ser configurada como a das parceiras, ou ficar tudo com o salão (100%).' : `Contrato de parceria (Lei 13.352/2016). O salão recebe tudo, retém a parte dele e repassa a cota de ${nm}.`}</p>
        <p className="gl-title">Cor na agenda</p>
        <div className="gl"><div className="gl-block"><span>Os horários de {nm} aparecem nesta cor na agenda</span>
          <div className="color-pick" role="radiogroup" aria-label={`Cor de ${p.name} na agenda`}>
            {AGENDA_COLORS.map((c) => <button key={c} role="radio" aria-checked={p.color === c} aria-label={COLOR_NAMES[c] ?? c} title={COLOR_NAMES[c]} className={p.color === c ? "on" : ""} style={{ background: c }} onClick={() => save({ color: c })} />)}
          </div></div></div>
        <p className="gl-title">Cota de {nm}<Saved at={savedAt} /></p>
        <div className="gl">
          <div className="gl-block"><span>Tipo</span><CfgChips value={c.kind} options={[['fixed', 'Fixa'], ['tiered', 'Escalonada pela produção']]} onChange={(v) => save({ cota: v === 'fixed' ? { kind: 'fixed', bps: tiers[0]!.bps } : { kind: 'tiered', tiers } })} /></div>
          {c.kind === 'fixed' ? <CfgNum label="Cota da parceira" unit="%" value={c.bps / 100} lim={[0, 100, 1]} onChange={(v) => save({ cota: { kind: 'fixed', bps: v * 100 } })} hint={`o salão retém ${100 - c.bps / 100}%`} /> : (
            <>
              {tiers.map((t, i) => {
                const from = i ? tiers[i - 1]!.upToCents ?? 0 : 0, last = i === tiers.length - 1;
                return (
                  <div key={i} className="pt-faixa">
                    <p>{last ? `Acima de ${moneyShort(from)}` : i ? `De ${moneyShort(from)} a ${moneyShort(t.upToCents!)}` : `Até ${moneyShort(t.upToCents!)}`}
                      {tiers.length > 2 && i > 0 && <button aria-label="Tirar esta faixa" onClick={() => { const n = tiers.filter((_, k) => k !== i); n[n.length - 1] = { ...n[n.length - 1]!, upToCents: null }; setTiers(n); }}>Tirar</button>}</p>
                    {!last && <CfgNum label="Limite da faixa" kind="money" value={t.upToCents!} lim={[from + 10_000, 10_000_000, 50_000]} onChange={(v) => setTiers(tiers.map((x, k) => (k === i ? { ...x, upToCents: v } : x)))} />}
                    <CfgNum label="Cota da parceira" unit="%" value={t.bps / 100} lim={[10, 95, 1]} onChange={(v) => setTiers(tiers.map((x, k) => (k === i ? { ...x, bps: v * 100 } : x)))} />
                  </div>
                );
              })}
              <button className="gl-row gl-btn ac-invite" onClick={() => {
                if (tiers.length >= 5) return toast('No máximo 5 faixas.', 'warn');
                const prev = tiers[tiers.length - 2]?.upToCents ?? 0;
                const n = tiers.map((x, k) => (k === tiers.length - 1 ? { ...x, upToCents: prev + 300_000 } : x));
                n.push({ upToCents: null, bps: Math.min(9500, tiers.at(-1)!.bps + 500) });
                setTiers(n);
              }}><b>+ Adicionar faixa</b></button>
              <p className="m-hint rg-hint">A faixa vale para o atendimento conforme a produção do mês até ali.</p>
            </>
          )}
          <CfgNum label="Venda de produtos" unit="%" value={p.productBps / 100} lim={[0, 50, 1]} onChange={(v) => save({ productBps: v * 100 })} hint="da venda que ela fizer" />
          <p className="m-hint rg-hint">A gorjeta vai inteira para {nm}.</p>
        </div>
        <p className="gl-title">Bônus por meta</p>
        <div className="gl">
          <CfgToggle label="Bônus quando bate a meta do mês" value={b.enabled} onChange={(v) => save({ bonus: { ...b, enabled: v } })} />
          {b.enabled && <>
            <CfgNum label="Meta de produção" kind="money" value={b.goalCents} lim={[50_000, 10_000_000, 50_000]} onChange={(v) => save({ bonus: { ...b, goalCents: v } })} />
            <div className="gl-block"><span>Bônus</span><CfgChips value={b.kind} options={[['value', 'Valor fixo'], ['pct', '% da produção']]} onChange={(v) => save({ bonus: { ...b, kind: v } })} /></div>
            {b.kind === 'value' ? <CfgNum label="Valor do bônus" kind="money" value={b.valueCents} lim={[1000, 500_000, 1000]} onChange={(v) => save({ bonus: { ...b, valueCents: v } })} />
              : <CfgNum label="Bônus" unit="%" value={b.bps / 100} lim={[1, 30, 1]} onChange={(v) => save({ bonus: { ...b, bps: v * 100 } })} />}
          </>}
        </div>
        <p className="gl-title">Repasse</p>
        <div className="gl">
          <div className="gl-block"><span>De quanto em quanto tempo</span><CfgChips value={p.payoutPeriod} options={[['semanal', 'Semanal'], ['quinzenal', 'Quinzenal'], ['mensal', 'Mensal']]} onChange={(v) => save({ payoutPeriod: v })} /></div>
          <CfgText label="Chave Pix" value={p.pixKey} onChange={(v) => save({ pixKey: v })} placeholder="para receber o repasse" />
          <CfgText label="CPF ou CNPJ (MEI)" value={p.document} onChange={(v) => save({ document: v })} placeholder="000.000.000-00" />
        </div>
        {!p.isOwner && <CfgToggle label="Ativa na agenda" value={p.active} onChange={(v) => save({ active: v })} sub="desativada não aparece para marcar; o histórico continua" />}
        {!p.isOwner && <p className="m-hint">Pela lei, a parceria pode ser encerrada por qualquer lado com aviso de pelo menos 30 dias. O histórico de {nm} continua guardado.</p>}
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

// ---------- bloqueio (folga, férias ou algumas horas) ----------
export { BloqueioSheet } from '../sheets/BloqueioSheet.tsx';
